import React from 'react';
import { DRAG_AREA, DRAG_HANDLE_VISIBILITY } from '../constants';
import PropTypes from 'prop-types';
import { HorizontalDragHandle as HorizontalDragHandleCE } from '@wix/wix-ui-icons-common/classic-editor/system';
import { HorizontalDragHandle as HorizontalDragHandleRE } from '@wix/wix-ui-icons-common/responsive-editor/system';
import { WixBaseUiEnvironmentContext, WixBaseUiEnvironmentProvider } from '../../../util/WixBaseUiEnvironment/WixBaseUiEnvironment';
import compose from '../../../hoc/compose';
import classNames from 'classnames';
import ArrowExpanded from '../../treeSectionDivider/arrowExpanded';
import TextLabel from '../../textLabel/textLabel';
import _ from 'lodash';
var COLLAPSIBLE_STATES = {
  COLLAPSED: 'collapsed',
  EXPANDED: 'expanded',
  NONE: 'none'
};
var TreeListRow = function TreeListRow(props) {
  var item = props.item,
    depth = props.depth,
    readOnly = props.readOnly,
    isPlaceholder = props.isPlaceholder,
    isPreview = props.isPreview,
    isDragging = props.isDragging,
    dragArea = props.dragArea,
    dragHandleVisibility = props.dragHandleVisibility,
    connectDragSource = props.connectDragSource,
    _props$showCollapseAr = props.showCollapseArrow,
    showCollapseArrow = _props$showCollapseAr === void 0 ? true : _props$showCollapseAr;
  var id = item.id,
    label = item.label,
    dataHook = item.dataHook,
    className = item.className,
    _item$draggable = item.draggable,
    draggable = _item$draggable === void 0 ? true : _item$draggable,
    selected = item.selected,
    shouldScrollOnMount = item.shouldScrollOnMount,
    onClick = item.onClick,
    onDoubleClick = item.onDoubleClick,
    onCollapseClick = item.onCollapseClick,
    icon = item.icon,
    indications = item.indications,
    suffix = item.suffix,
    alwaysShowSuffix = item.alwaysShowSuffix,
    disabled = item.disabled;
  var ref = /*#__PURE__*/React.createRef();
  var context = React.useContext(WixBaseUiEnvironmentContext);
  var canDrag = draggable && !readOnly;
  var underDragging = isDragging && !isPreview;
  var collapseState = COLLAPSIBLE_STATES.NONE;
  if (!_.isEmpty(item.children)) {
    collapseState = item.isCollapsed ? COLLAPSIBLE_STATES.COLLAPSED : COLLAPSIBLE_STATES.EXPANDED;
  }
  React.useEffect(function () {
    var element = ref.current;
    if (element) {
      // method is not supported by all browsers
      element.scrollIntoViewIfNeeded ? element.scrollIntoViewIfNeeded() : element.scrollIntoView(false);
    }
  }, []);
  var renderHandle = function renderHandle() {
    if (readOnly) {
      return;
    }
    var handle = /*#__PURE__*/React.createElement("div", {
      className: classNames('tree-list-item-handle', canDrag && 'move', {
        disabled: !draggable,
        'show-handle': isPreview || dragHandleVisibility === DRAG_HANDLE_VISIBILITY.ALWAYS
      }),
      "data-hook": "tree-list-item-handle",
      id: item.id
    }, context.theme === 'responsive' ? /*#__PURE__*/React.createElement(HorizontalDragHandleRE, null) : /*#__PURE__*/React.createElement(HorizontalDragHandleCE, null));
    if (dragArea === DRAG_AREA.HANDLE && draggable) {
      return connectDragSource(handle);
    }
    return handle;
  };

  // during drag item is rendered outside BaseUI root, so skin is not applicable
  var wrapWithProviderIfNeeded = function wrapWithProviderIfNeeded(body) {
    return isPreview ? /*#__PURE__*/React.createElement(WixBaseUiEnvironmentProvider, context, body) : body;
  };
  var handleRowClick = function handleRowClick() {
    if (onClick) {
      onClick(id);
    }
  };
  var handleArrowClick = function handleArrowClick() {
    if (onCollapseClick && collapseState !== COLLAPSIBLE_STATES.NONE) {
      onCollapseClick(id);
    }
  };
  var handleTextClick = function handleTextClick() {
    if (onDoubleClick) {
      onDoubleClick(id);
    }
  };
  return wrapWithProviderIfNeeded( /*#__PURE__*/React.createElement("div", {
    /* All data-attributes required only for tests purpose */
    "data-hook": dataHook || "tree-list-row-".concat(id),
    "data-depth": depth,
    "data-draggable-source": canDrag,
    "data-id": id,
    className: classNames('tree-list-item', className, 'medium', {
      selected: selected,
      underDragging: underDragging,
      placeholder: isPlaceholder,
      highlight: isPreview,
      dragging: isPreview,
      disabled: disabled
    }),
    ref: shouldScrollOnMount ? ref : undefined,
    onClick: handleRowClick
  }, !isPlaceholder && /*#__PURE__*/React.createElement(React.Fragment, null, renderHandle(), showCollapseArrow && /*#__PURE__*/React.createElement("div", {
    "data-hook": "tree-list-item-arrow",
    className: "tree-list-item-arrow-wrapper",
    onClick: handleArrowClick
  }, /*#__PURE__*/React.createElement("div", {
    className: "tree-list-item-arrow ".concat(collapseState)
  }, /*#__PURE__*/React.createElement(ArrowExpanded, null))), icon && /*#__PURE__*/React.createElement("div", {
    className: "tree-list-item-icon"
  }, icon), typeof label === 'string' ? /*#__PURE__*/React.createElement("div", {
    className: "tree-list-item-text-wrapper",
    onDoubleClick: handleTextClick
  }, /*#__PURE__*/React.createElement("div", {
    className: "tree-list-item-text"
  }, /*#__PURE__*/React.createElement(TextLabel, {
    dataHook: "tree-list-item-label",
    value: label,
    type: "T20",
    shouldTranslate: false,
    disabled: disabled
  }))) : label, indications && indications.map(function (node, i) {
    return /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "tree-list-item-indication"
    }, node);
  }), suffix && /*#__PURE__*/React.createElement("div", {
    className: classNames('tree-list-item-suffix', 'reserve-space', {
      'show-suffix': alwaysShowSuffix
    })
  }, suffix))));
};
TreeListRow.displayName = 'TreeListRow';
TreeListRow.propTypes = {
  item: PropTypes.object.isRequired,
  depth: PropTypes.number.isRequired,
  readOnly: PropTypes.bool.isRequired,
  isPlaceholder: PropTypes.bool.isRequired,
  isPreview: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool.isRequired,
  dragArea: PropTypes.oneOf(['all', 'handle']).isRequired,
  dragHandleVisibility: PropTypes.oneOf(['always', 'hover']).isRequired,
  connectDragSource: PropTypes.func.isRequired,
  showCollapseArrow: PropTypes.bool
};
export default compose(TreeListRow);